import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TrainingCourseRequestOptions } from '@models/types'

const watchEntityKeys = ['trainingCourses']

export type TrainingCoursesFilters = {
  string?: string,
}

interface UseTrainingCoursesOptions {
  entityKey?: string,
  filters?: TrainingCoursesFilters,
  performHttpRequests?: boolean,
  requestOptions?: TrainingCourseRequestOptions,
}

function useTrainingCourses(options: UseTrainingCoursesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    string: filterString,
  } = filters

  const {
    updatedEntities: { trainingCourses: coursesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const trainingCourses = useSelector(reduxState => reduxState.entities.trainingCourses)

  const filteredTrainingCourses = useMemo(() => {
    const filtered = Object.values(trainingCourses).filter((trainingCourse) => {
      const { id, title } = trainingCourse

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [coursesUpdatedAt, JSON.stringify(filters)])

  const filteredTrainingCoursesCount = filteredTrainingCourses.length
  const hasFilteredTrainingCourses = !!filteredTrainingCoursesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTrainingCoursesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTrainingCourses } = useReduxAction(
    'trainingCourses',
    'loadTrainingCourses',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTrainingCourses,
    filteredTrainingCoursesCount,
    hasFilteredTrainingCourses,
    loading: loadingTrainingCourses,
  }
}

export default useTrainingCourses
